/**
 * Quality engine — frontend service.
 *
 * Mirrors the API in backend/routes/private/qualityRoutes.js. Used by the
 * dashboard QualityPromptsPanel and (later) the per-rendition thumbs widget.
 */
import api from './api'

export type PromptType =
  | 'clarify_context'
  | 'review_rendition'
  | 'review_stale_rendition'
  | 'regenerate_context'
  | 'connect_integration'
  | 'upload_asset'

export type TriggerKind =
  | 'confidence'
  | 'rendition_edit'
  | 'rendition_outcome'
  | 'thumbs'
  | 'periodic'
  | 'cascade'

export type PromptState =
  | 'queued' | 'shown' | 'answered' | 'applying' | 'complete' | 'dismissed' | 'expired'

export type GenerationStrategy = 'template' | 'ai'

export type QuestionKind = 'short_text' | 'long_text' | 'enum' | 'thumbs' | 'directive'

export interface QualityPromptPayload {
  question: string
  kind: QuestionKind
  examples?: string[] | null
  placeholder?: string | null
  options?: Array<{ value: string; label: string }> | null
  /** For `directive` kind: the call-to-action button + route to navigate to.
   *  `route` may include a `:workspaceId` placeholder that the client substitutes. */
  cta?: { label: string; route: string } | null
  reason?: string | null
  questionTemplateId?: string | null
  renditionChannel?: string | null
  /** One red line under the field — e.g. why the seed question is back
   *  after a failed generation run. */
  notice?: string | null
  /** The previous attempt's answer, pre-filled into the field when the
   *  question returns after a failed build. */
  previousAnswer?: string | null
}

export interface QualityPrompt {
  id: number
  type: PromptType
  targetEntityType: string | null
  triggerKind: TriggerKind
  triggerRefId: number | null
  payload: QualityPromptPayload
  generationStrategy: GenerationStrategy
  priority: number
  state: PromptState
  createdAt: string
  shownAt: string | null
}

export interface ListPromptsResponse {
  prompts: QualityPrompt[]
}

export interface AnswerResponse {
  ok: boolean
  refineModel: string | null
  refineConfidence: number | null
  downstreamPromptsEnqueued: number
  renditionsFlagged: number
}

export interface StrategyJobSummary {
  sessionId: string
  status: 'running' | 'complete' | 'failed'
  progressPct: number
  etaSeconds: number | null
  error: string | null
  completedAt: number | null
}

/** Precomputed verdict for the "Build your Business Brain" questions section.
 *  Computed by a backend background job (heavy evaluation never runs per
 *  request) and read verbatim by the page — see backend
 *  qualityEngine/statusSnapshot.js. The results row at the top has its own
 *  independent flag, `resultsReady`. */
export type QualityBanner =
  | 'questions' | 'building_strategy' | 'none'

export interface QualityStatus {
  banner: QualityBanner
  openPrompts: number
  phase1Complete: boolean
  /** Show the dashboard's results row: strategy build finished, or the
   *  workspace has ever had activity. */
  resultsReady: boolean
  strategyJob: StrategyJobSummary | null
  computedAt: string
}

export async function getStatus(workspaceId: string | number): Promise<QualityStatus> {
  const { data } = await api.get<QualityStatus>(
    `/private/workspaces/${workspaceId}/quality/status`,
  )
  return data
}

/** Review aid (?allStates=1): every question the engine can ask this
 *  workspace, copy resolved server-side exactly as production renders it. */
export interface CatalogQuestion {
  entityType: string
  payload: QualityPromptPayload
}

export async function getQuestionCatalog(
  workspaceId: string | number,
): Promise<{ questions: CatalogQuestion[] }> {
  const { data } = await api.get<{ questions: CatalogQuestion[] }>(
    `/private/workspaces/${workspaceId}/quality/question-catalog`,
  )
  return data
}

export async function listPrompts(
  workspaceId: string | number,
  opts: { surface?: 'dashboard' | 'preflight'; limit?: number } = {},
): Promise<ListPromptsResponse> {
  const params = new URLSearchParams()
  if (opts.surface) params.set('surface', opts.surface)
  if (opts.limit) params.set('limit', String(opts.limit))
  const { data } = await api.get<ListPromptsResponse>(
    `/private/workspaces/${workspaceId}/quality/prompts?${params.toString()}`,
  )
  return data
}

export async function answerPrompt(promptId: number, answer: string): Promise<AnswerResponse> {
  const { data } = await api.post<AnswerResponse>(
    `/private/quality/prompts/${promptId}/answer`,
    { answer },
  )
  return data
}

export async function dismissPrompt(promptId: number, reason?: string): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>(
    `/private/quality/prompts/${promptId}/dismiss`,
    { reason },
  )
  return data
}

export async function recordPromptEvent(
  promptId: number,
  eventType: 'opened' | 'skipped',
  surface: 'dashboard' | 'preflight' = 'dashboard',
): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>(
    `/private/quality/prompts/${promptId}/event`,
    { eventType, surface },
  )
  return data
}

export async function recordThumbs(
  renditionId: number,
  direction: 'up' | 'down',
  text?: string,
): Promise<{ ok: boolean; promptsEnqueued: number }> {
  const { data } = await api.post<{ ok: boolean; promptsEnqueued: number }>(
    `/private/renditions/${renditionId}/thumbs`,
    { direction, text },
  )
  return data
}
